import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import type { AudienceExtractionAssetDto } from '@inference.engine/api-client/web';
import { FILE_SIZE_LIMITS } from '@inference.engine/file-utils/web';
import { getFileUploadUrl, uploadFileToS3 } from '@/shared/lib/file-upload';
import { i18n } from '@/shared/i18n';
import {
  getAudienceExtraction,
  retryAudienceExtraction,
  startAudienceExtraction,
} from '../../api/audience-extraction.api';
import {
  isExtractionInFlight,
  useAnalysisFormStore,
  type ExtractedAudience,
} from '../analysisFormStore';

/** How often the run is asked about while it is still reading. */
const POLL_INTERVAL_MS = 2_000;

/** Matches CLAIM_LEASE_MS in the API's audience-extraction.service.ts, which reclaims on it. */
const STALE_CLAIM_MS = 10 * 60 * 1000;

/** Mirrors EXTRACTION_ASSETS_MAX in the API's audience-extraction.dto.ts, which rejects more. */
export const EXTRACTION_FILES_MAX = 5;

/**
 * Mirrors EXTRACTION_BYTES_MAX in the same DTO: what one read may hand the model.
 *
 * Far below what storage accepts per file, so the file count alone does not imply it - five
 * documents inside the count can be well over this, and two ordinary decks already are.
 */
export const EXTRACTION_BYTES_MAX = FILE_SIZE_LIMITS.LLM.DEFAULT;

/**
 * Mirrors every cap on `UploadedAudienceSegmentDto` in the API's analysis.dto.ts.
 *
 * Clipped here rather than on the way out, so the cards show exactly what will be sent. The
 * DTO refuses an overrun outright, and it is read at the last click of the wizard: one long
 * field would fail the whole submission, minutes after the reading everyone waited for. The
 * extractor's own schema puts no bound on any of these, so nothing upstream prevents it.
 *
 * A clipped field costs a sentence. A refused submission costs the report.
 */
const SEGMENT_MAX = {
  name: 120,
  description: 500,
  ageRange: 50,
  tags: 10,
  context: 20_000,
} as const;

const SOURCE_FILE_NAME_MAX = 255;

const clipped = (value: string | undefined, max: number): string | undefined =>
  value === undefined ? undefined : value.slice(0, max);

/**
 * What to tell the presign endpoint a file is.
 *
 * Its `contentType` is a closed list, and a browser reports nothing at all for some of the
 * extensions this tab accepts - so an empty type is resolved from the extension rather than
 * sent as-is, which the endpoint would refuse.
 */
const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  txt: 'text/plain',
  md: 'text/markdown',
};

const contentTypeOf = (file: File): string => {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return file.type || MIME_BY_EXTENSION[extension] || 'text/plain';
};

/**
 * Which document an audience was read out of, where there is an honest answer.
 *
 * The extractor is told to leave this out for an audience that several documents describe, so
 * a run that read more than one has nothing to fall back on. Naming every file it read would
 * claim a provenance the audience does not have, and five real file names joined together run
 * past what the report accepts - which fails the whole submission at the last click of the
 * wizard, minutes after the reading everyone waited for.
 */
const sourceFileNameOf = (
  reported: string | undefined,
  fileNames: string[],
): string | undefined => {
  const name = reported ?? (fileNames.length === 1 ? fileNames[0] : undefined);
  return name?.slice(0, SOURCE_FILE_NAME_MAX);
};

const toAsset = async (file: File): Promise<AudienceExtractionAssetDto> => {
  const mimeType = contentTypeOf(file);
  const { url, fields, assetId } = await getFileUploadUrl(mimeType, file.name);

  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
  formData.append('file', file);
  await uploadFileToS3(url, formData);

  return { assetId, mimeType, fileName: file.name, size: file.size };
};

/**
 * Uploads documents, asks IQ to read them, and follows the run until it settles.
 *
 * Nothing here is local state. The run and the segments it produces both live in the form
 * store, so the reading survives this tab being unmounted - which the wizard does on every
 * Next, and the audience step does on a switch between sources. Mounting again while a run is
 * still going picks the polling back up rather than abandoning it; the query is enabled off
 * the stored run, so that needs no separate resume.
 */
export const useAudienceExtraction = () => {
  const extraction = useAnalysisFormStore((s) => s.extraction);
  const patchExtractionRun = useAnalysisFormStore((s) => s.patchExtractionRun);
  const addExtractedAudiences = useAnalysisFormStore((s) => s.addExtractedAudiences);

  useQuery({
    queryKey: ['audience-extraction', extraction.id],
    enabled: extraction.id !== null && isExtractionInFlight(extraction),
    refetchInterval: POLL_INTERVAL_MS,
    queryFn: async () => {
      const run = await getAudienceExtraction(extraction.id as string);

      if (run.status === 'failed') {
        patchExtractionRun({ status: 'failed', error: run.error });
        return run;
      }

      // A claim older than the server's lease belonged to a process that died: the run says
      // `processing` with nothing left to finish it, and the server would never say `failed`.
      // Calling it over here is what puts the retry in front of the reader - and the retry
      // works, because the same lease is what lets the server take the run back.
      //
      // `pending` counts for the same reason, against the client's own clock: a run whose
      // claim never landed is never dated, so measuring only the server's date left that one
      // polling forever. Not widened past the two in-flight statuses though - a `ready` run
      // is delivered below, and calling it dead first would drop the segments it holds.
      const startedAt = run.startedAt
        ? Date.parse(run.startedAt)
        : useAnalysisFormStore.getState().extraction.waitingSince;
      if (
        (run.status === 'processing' || run.status === 'pending') &&
        startedAt !== undefined &&
        Date.now() - startedAt > STALE_CLAIM_MS
      ) {
        patchExtractionRun({
          status: 'failed',
          error: i18n.t('analysis:analysis.uploadDocumentTab.theReadStopped'),
        });
        return run;
      }

      if (run.status === 'ready') {
        // Delivered once per run. Without this the segments are written again on every poll
        // that answers `ready`, and again on a remount, clearing a selection as it is made.
        // Read from the store rather than the closure: this poll outlives the render that
        // started it, and an earlier one may already have handed the same run over.
        const delivered = useAnalysisFormStore.getState().extraction.deliveredRunId === run.id;
        if (!delivered) {
          addExtractedAudiences(
            (run.segments ?? []).map(
              (segment): ExtractedAudience => ({
                id: uuidv4(),
                name: segment.name.slice(0, SEGMENT_MAX.name),
                description: clipped(segment.description, SEGMENT_MAX.description),
                ageRange: clipped(segment.ageRange, SEGMENT_MAX.ageRange),
                // The type says required because the extractor's schema does, but this is a
                // network response: a run stored before the field existed, or a model that
                // answered without it, arrives undefined and `.slice` would throw.
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- see above
                tags: segment.tags?.slice(0, SEGMENT_MAX.tags),
                context: clipped(segment.context, SEGMENT_MAX.context),
                sourceFileName: sourceFileNameOf(segment.sourceFileName, extraction.fileNames),
                sourceLocation: segment.sourceLocation,
              }),
            ),
          );
        }
        patchExtractionRun({ status: 'ready', deliveredRunId: run.id });
      }

      return run;
    },
  });

  // Another document adds to what is already on screen rather than replacing it: the report
  // is built from everything handed over, and the picks made from the first document survive.
  const extract = useCallback(
    async (files: File[]) => {
      // The previous run is dropped before the upload starts: it would otherwise still be
      // polled, answer `ready`, and hand its own segments over under this upload's file names.
      patchExtractionRun({
        id: null,
        status: 'uploading',
        fileNames: files.map((file) => file.name),
        files,
        deliveredRunId: null,
        error: undefined,
      });

      try {
        const assets = await Promise.all(files.map(toAsset));
        const run = await startAudienceExtraction(assets);
        patchExtractionRun({ id: run.id, status: 'reading', waitingSince: Date.now() });
      } catch (error: unknown) {
        patchExtractionRun({
          status: 'failed',
          error: error instanceof Error ? error.message : undefined,
        });
      }
    },
    [patchExtractionRun],
  );

  const retry = useCallback(async () => {
    const { id, files } = useAnalysisFormStore.getState().extraction;

    // No run means it never started - the upload or the request itself failed - so the retry is
    // the whole thing again, not a re-read of something that does not exist.
    if (!id) {
      if (files.length > 0) await extract(files);
      return;
    }

    try {
      // Seeded from the reply rather than flipped to `reading` first. The API awaits the
      // claim before answering precisely so a caller does not have to guess, and `reading`
      // is what enables the poll: setting it early let a cold cache fetch immediately, read
      // the row as it was before the claim landed, and write that `failed` back over a run
      // that had in fact just started. A `ready` run is handed to the poll the same way, so
      // its segments arrive through the one path that delivers them once.
      const run = await retryAudienceExtraction(id);
      patchExtractionRun(
        run.status === 'failed'
          ? { status: 'failed', error: run.error ?? undefined }
          : { status: 'reading', error: undefined, waitingSince: Date.now() },
      );
    } catch (error: unknown) {
      patchExtractionRun({
        status: 'failed',
        error: error instanceof Error ? error.message : undefined,
      });
    }
  }, [extract, patchExtractionRun]);

  return {
    status: extraction.status,
    fileNames: extraction.fileNames,
    error: extraction.error,
    extract,
    retry,
  };
};
